import React from 'react';
import { Container } from 'react-bootstrap';

const TermsOfServicePage = () => {
  return (
    <Container className="py-5" style={{ maxWidth: '900px' }}>
      <div className="legal-document">
        <h1 className="mb-4">Terms of Service</h1>
        
        <p className="text-muted mb-4">
          <strong>Last Updated: January 01, 2026</strong>
        </p>

        <section className="mb-5">
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing or using Better Business Booking ("Triple B", "we", "us", or "our") services, including our website, mobile applications, and related services (collectively, the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not access or use the Service.
          </p>
          <p>
            These Terms constitute a legally binding agreement between you and Better Business Booking. If you are using the Service on behalf of a business or organization, you represent and warrant that you have the authority to bind that business or organization to these Terms, and "you" will refer to that business or organization.
          </p>
        </section>

        <section className="mb-5">
          <h2>2. Description of Service</h2>
          <p>
            Better Business Booking is a Software-as-a-Service (SaaS) platform that provides appointment scheduling, booking management, and related business tools. The Service allows businesses to:
          </p>
          <ul>
            <li>Create and manage business profiles</li>
            <li>Set up services and availability schedules</li>
            <li>Accept and manage customer appointments</li>
            <li>Send notifications and reminders to customers</li>
            <li>Integrate with third-party calendar systems</li>
            <li>Process payments for services</li>
            <li>Access analytics and reporting features</li>
            <li>Integrate with accounting software</li>
          </ul>
        </section>

        <section className="mb-5">
          <h2>3. Account Registration and Security</h2>
          <h3>3.1 Account Creation</h3>
          <p>
            To use the Service, you must create an account by providing accurate, current, and complete information. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
          </p>
          <h3>3.2 Account Requirements</h3>
          <ul>
            <li>You must be at least 18 years old to create an account</li>
            <li>You must provide a valid email address</li>
            <li>You must choose a strong password (minimum 8 characters)</li>
            <li>You must verify your email address to activate your account</li>
            <li>You may not create multiple accounts to circumvent service limitations</li>
          </ul>
          <h3>3.3 Account Security</h3>
          <p>You are responsible for:</p>
          <ul>
            <li>Maintaining the security of your account credentials</li>
            <li>All activities that occur under your account</li>
            <li>Notifying us immediately of any unauthorized access or use</li>
            <li>Ensuring that anyone who accesses your account is authorized to do so</li>
          </ul>
          <p>We are not liable for any loss or damage arising from your failure to comply with these security obligations.</p>
        </section>

        <section className="mb-5">
          <h2>4. Subscription Plans and Payment Terms</h2>
          <h3>4.1 Subscription Plans</h3>
          <p>
            The Service is offered through various subscription plans with different features, limitations, and pricing. Current subscription plans include:
          </p>
          <ul>
            <li><strong>Basic Plan</strong>: Limited features and usage</li>
            <li><strong>Professional Plan</strong>: Enhanced features and higher usage limits</li>
            <li><strong>Enterprise Plan</strong>: Full feature access with unlimited usage</li>
          </ul>
          <p>Subscription plans, features, and pricing are subject to change. We will provide reasonable notice of any material changes to pricing.</p>
          <h3>4.2 Payment Terms</h3>
          <ul>
            <li>Subscription fees are billed in advance on a monthly or annual basis</li>
            <li>All fees are non-refundable except as required by law or as explicitly stated in these Terms</li>
            <li>You authorize us to charge your payment method for all fees due</li>
            <li>If payment fails, we may suspend or terminate your access to the Service</li>
            <li>You are responsible for all applicable taxes</li>
          </ul>
          <h3>4.3 Automatic Renewal</h3>
          <p>
            Subscriptions automatically renew at the end of each billing period unless you cancel before the renewal date. You may cancel your subscription at any time through your account settings. Cancellation will take effect at the end of your current billing period.
          </p>
        </section>

        <section className="mb-5">
          <h2>5. Acceptable Use Policy</h2>
          <h3>5.1 Permitted Use</h3>
          <p>You may use the Service only for lawful purposes and in accordance with these Terms. You agree to use the Service to:</p>
          <ul>
            <li>Manage legitimate business appointments and bookings</li>
            <li>Provide accurate and truthful information</li>
            <li>Comply with all applicable laws and regulations</li>
            <li>Respect the rights of others</li>
          </ul>
          <h3>5.2 Prohibited Activities</h3>
          <p>You agree NOT to:</p>
          <ul>
            <li>Violate any applicable local, state, national, or international law or regulation</li>
            <li>Infringe upon the intellectual property rights of others</li>
            <li>Transmit any malicious code, viruses, or harmful software</li>
            <li>Attempt to gain unauthorized access to the Service or related systems</li>
            <li>Interfere with or disrupt the Service or servers</li>
            <li>Use the Service to send spam, unsolicited messages, or harass others</li>
            <li>Impersonate any person or entity</li>
            <li>Collect or harvest information about other users</li>
            <li>Use automated systems (bots, scrapers) to access the Service without permission</li>
            <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
            <li>Use the Service for any illegal or unauthorized purpose</li>
            <li>Violate the privacy rights of others</li>
            <li>Use the Service to compete with or harm our business</li>
          </ul>
        </section>

        <section className="mb-5">
          <h2>6. Customer Data and Privacy</h2>
          <p>
            When customers book appointments through your business profile, they may provide personal information. You are responsible for obtaining necessary consents, complying with applicable privacy laws (GDPR, CCPA, etc.), and protecting customer data. We process customer data on your behalf as a data processor. You are the data controller and are responsible for ensuring that your use of the Service complies with applicable data protection laws.
          </p>
        </section>

        <section className="mb-5">
          <h2>7. Third-Party Integrations</h2>
          <p>
            The Service may integrate with third-party services (Google Calendar, Microsoft Outlook, Stripe, QuickBooks, SMS providers, etc.). Your use of these integrations is subject to the terms and conditions of those third-party services. We are not responsible for the availability or functionality of third-party services.
          </p>
        </section>

        <section className="mb-5">
          <h2>8. Intellectual Property Rights</h2>
          <p>
            The Service, including all content, features, functionality, and technology, is owned by Better Business Booking and is protected by copyright, trademark, patent, and other intellectual property laws. You retain ownership of any content you create or upload to the Service, and you grant us a worldwide, non-exclusive, royalty-free license to use, store, and display your content to provide the Service.
          </p>
        </section>

        <section className="mb-5">
          <h2>9. Service Availability and Modifications</h2>
          <p>
            We strive to provide reliable service but do not guarantee that the Service will be available at all times. We reserve the right to modify, suspend, or discontinue any part of the Service at any time. We will provide reasonable notice of material changes when possible.
          </p>
        </section>

        <section className="mb-5">
          <h2>10. Limitation of Liability</h2>
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND. TO THE MAXIMUM EXTENT PERMITTED BY LAW, BETTER BUSINESS BOOKING SHALL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
          </p>
        </section>

        <section className="mb-5">
          <h2>11. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless Better Business Booking, its affiliates, officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, costs, or expenses arising from your use of the Service, your violation of these Terms, or your violation of any third-party rights or applicable laws.
          </p>
        </section>

        <section className="mb-5">
          <h2>12. Termination</h2>
          <p>
            You may terminate your account at any time by canceling your subscription or contacting us at bbb@fringe.ltd. We may suspend or terminate your access immediately if you violate these Terms, fail to pay subscription fees, or engage in fraudulent or illegal activity. Upon termination, your right to use the Service immediately ceases, and we may delete your account and data subject to legal retention requirements.
          </p>
        </section>

        <section className="mb-5">
          <h2>13. Dispute Resolution</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the United States. In the event of any dispute, you agree to first contact us to seek an informal resolution. If informal resolution fails, disputes may be resolved through mediation or binding arbitration in accordance with the rules of the American Arbitration Association.
          </p>
        </section>

        <section className="mb-5">
          <h2>14. General Provisions</h2>
          <p>
            We reserve the right to modify these Terms at any time. Material changes will be communicated through email notification, notice on our website, or within the Service. Your continued use of the Service after changes become effective constitutes acceptance of the modified Terms.
          </p>
        </section>

        <section className="mb-5">
          <h2>15. Contact Information</h2>
          <p>
            For questions about these Terms, please contact us at:
          </p>
          <p>
            <strong>Better Business Booking</strong><br />
            Email: <a href="mailto:bbb@fringe.ltd">bbb@fringe.ltd</a><br />
            Website: <a href="https://bbb.fringe.ltd" target="_blank" rel="noopener noreferrer">https://bbb.fringe.ltd</a>
          </p>
        </section>

        <section className="mb-5">
          <h2>16. Acknowledgment</h2>
          <p className="fw-bold">
            BY USING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS OF SERVICE.
          </p>
        </section>
      </div>

      <style>{`
        .legal-document {
          line-height: 1.8;
        }
        .legal-document h1 {
          color: #333;
          font-size: 2.5rem;
          font-weight: bold;
        }
        .legal-document h2 {
          color: #444;
          font-size: 1.5rem;
          margin-top: 2rem;
          margin-bottom: 1rem;
          font-weight: 600;
        }
        .legal-document h3 {
          color: #555;
          font-size: 1.2rem;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          font-weight: 600;
        }
        .legal-document ul {
          margin-left: 1.5rem;
          margin-bottom: 1rem;
        }
        .legal-document li {
          margin-bottom: 0.5rem;
        }
        .legal-document p {
          margin-bottom: 1rem;
        }
      `}</style>
    </Container>
  );
};

export default TermsOfServicePage;
